import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

interface Props {
  form: Invitation;
  onChange: <K extends keyof Invitation>(key: K, val: Invitation[K]) => void;
}

export default function EventDetailsTab({ form, onChange }: Props) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Event Details</h3>
        <p className="text-slate-400 text-xs">Date, time, location and special instructions for guests.</p>
      </div>

      {/* Date + Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Event Date
          </label>
          <input
            type="date"
            value={form.event_date ? form.event_date.slice(0, 10) : ""}
            onChange={(e) => onChange("event_date", e.target.value || null)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Event Time
          </label>
          <input
            type="text"
            value={form.event_time ?? ""}
            onChange={(e) => onChange("event_time", e.target.value || null)}
            placeholder="6:00 PM"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
          <p className="text-slate-400 text-xs mt-1">Free text, e.g. &quot;6:00 PM&quot; or &quot;18:00 hrs&quot;</p>
        </div>
      </div>

      {/* Venue */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Venue Name
        </label>
        <input
          type="text"
          value={form.venue_name ?? ""}
          onChange={(e) => onChange("venue_name", e.target.value || null)}
          placeholder="Jardín El Encanto"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
        />
      </div>

      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Venue Address
        </label>
        <input
          type="text"
          value={form.venue_address ?? ""}
          onChange={(e) => onChange("venue_address", e.target.value || null)}
          placeholder="Av. Principal 123, Col. Centro, Ciudad de México"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
        />
      </div>

      {/* Google Maps */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Google Maps URL
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={form.google_maps_url ?? ""}
            onChange={(e) => onChange("google_maps_url", e.target.value || null)}
            placeholder="https://maps.google.com/..."
            className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
          {form.google_maps_url && (
            <a
              href={form.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-map-pin-line"></i>
              </span>
              Test
            </a>
          )}
        </div>
        <p className="text-slate-400 text-xs mt-1">Guests will see a &quot;Get Directions&quot; button on the invitation.</p>
      </div>

      {/* Dress code + Gift registry */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Dress Code
          </label>
          <input
            type="text"
            value={form.dress_code ?? ""}
            onChange={(e) => onChange("dress_code", e.target.value || null)}
            placeholder="Formal / Black Tie / Casual"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Gift Registry URL
          </label>
          <input
            type="url"
            value={form.gift_registry_url ?? ""}
            onChange={(e) => onChange("gift_registry_url", e.target.value || null)}
            placeholder="https://mesaderegalos.com/..."
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
      </div>

      {/* Summary card */}
      {(form.event_date || form.venue_name) && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
          <p className="text-indigo-600 text-xs font-semibold uppercase tracking-wider mb-3">Event Summary Preview</p>
          <div className="space-y-2">
            {form.event_date && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-4 h-4 flex items-center justify-center text-indigo-400">
                  <i className="ri-calendar-2-line text-xs"></i>
                </span>
                {new Date(form.event_date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                {form.event_time && <span className="text-slate-400">· {form.event_time}</span>}
              </div>
            )}
            {form.venue_name && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-4 h-4 flex items-center justify-center text-indigo-400">
                  <i className="ri-map-pin-line text-xs"></i>
                </span>
                {form.venue_name}
                {form.venue_address && <span className="text-slate-400 text-xs">· {form.venue_address}</span>}
              </div>
            )}
            {form.dress_code && (
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-4 h-4 flex items-center justify-center text-indigo-400">
                  <i className="ri-shirt-line text-xs"></i>
                </span>
                {form.dress_code}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
